import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { WHATSAPP, PHONE, EMAIL } from "../mock";

export const WARRANTY_NOTE = "1 month testing warranty unless otherwise stated.";
export const STOCK_DESCRIPTION = "Pre-owned / open-box company assets.";

export const inr = (n) => "\u20b9" + Number(n || 0).toLocaleString("en-IN");
// PDF-safe money (core jsPDF fonts cannot render the rupee glyph \u20b9).
const inrPdf = (n) => "INR " + Number(n || 0).toLocaleString("en-IN");
const priceTextPdf = (p) => (p.price_on_request ? "On request" : inrPdf(p.price));
export const productUrl = (item) => `${window.location.origin}/product/${item.id}`;

// A compact, shareable selection link built from product codes + requested quantities.
// Format: CODE or CODE~QTY (qty only when >1), comma-separated. Codes are stable (no slashes).
export const selectionLink = (items) => {
  const parts = items
    .filter((i) => i.code)
    .map((i) => (i.qty && i.qty > 1 ? `${i.code}~${i.qty}` : i.code));
  return `${window.location.origin}/enquiry?codes=${encodeURIComponent(parts.join(","))}`;
};

// Keep single-URL WhatsApp messages from getting unwieldy on long lists.
const MAX_FULL = 5;

const line = (i, n) => {
  const q = i.qty ? ` (Qty: ${i.qty})` : "";
  const note = (i.excludes || "").trim() ? `\n   Note: ${i.excludes.trim()}` : "";
  return `${n}. ${i.code ? i.code + " \u2014 " : ""}${i.name}${q}${note}\n   ${productUrl(i)}`;
};

export const buildEnquiryText = (items) => {
  if (!items.length) return "";
  if (items.length <= MAX_FULL) {
    const body = items.map((i, idx) => line(i, idx + 1)).join("\n");
    return `Hi Kapatia, I'd like to enquire about these items:\n\n${body}\n\nPlease share availability and best price.`;
  }
  const codes = items.map((i) => `${i.code || i.name}${i.qty ? ` x${i.qty}` : ""}`).join(", ");
  return `Hi Kapatia, I'd like to enquire about ${items.length} items:\n${codes}\n\nFull list: ${selectionLink(items)}\n\nPlease share availability and best price.`;
};

export const buildWhatsAppUrl = (items) =>
  `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(buildEnquiryText(items))}`;

export const buildMailto = (items) => {
  const subject = `Stock enquiry - ${items.length} item${items.length === 1 ? "" : "s"}`;
  return `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildEnquiryText(items))}`;
};

const todayStr = () =>
  new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const GREEN = [27, 77, 62];
const ORANGE = [232, 80, 26];

// Branded, customer-safe PDF. Only public fields are rendered — never source data.
export const downloadCataloguePdf = ({ title, items, enquiry = true }) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();

  doc.setFillColor(...GREEN);
  doc.rect(0, 0, W, 54, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Kapatia Pvt Ltd", 40, 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Phone ${PHONE}   |   WhatsApp wa.me/${WHATSAPP}   |   ${EMAIL}   |   www.kapatia.com`, 40, 42);

  doc.setTextColor(...GREEN);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(title, 40, 78);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text(`${STOCK_DESCRIPTION}  ${WARRANTY_NOTE}`, 40, 94);
  doc.text(`Generated ${todayStr()}  |  ${items.length} item${items.length === 1 ? "" : "s"}`, 40, 108);

  // A specific warranty exception is anything other than the general testing warranty.
  const exceptionOf = (i) => {
    const w = (i.warranty_display || "").trim();
    if (!w) return "";
    if (/^standard$/i.test(w)) return "";
    if (w === WARRANTY_NOTE) return "";
    return w;
  };
  const reqQty = (i) => (i.qty != null ? i.qty : 1);

  const urls = items.map((i) => productUrl(i));
  const rows = items.map((i) => {
    const exc = exceptionOf(i);
    // Avoid "Warranty: Warranty…" when the value already begins with the word.
    let name = exc ? `${i.name}\n${/^warranty/i.test(exc) ? exc : "Warranty: " + exc}` : i.name;
    const excl = (i.excludes || "").trim();
    if (excl) name += `\nNote: ${excl}`;
    return [
      i.code || "-",
      name,
      i.condition || "Pre-owned / open-box",
      enquiry ? String(reqQty(i)) : (i.qty_available != null ? String(i.qty_available) : "-"),
      priceTextPdf(i),
      "View product",
    ];
  });

  autoTable(doc, {
    startY: 120,
    showHead: "everyPage",
    head: [["Code", "Item & configuration", "Condition", enquiry ? "Requested qty" : "In stock", "Unit price", "Product"]],
    body: rows,
    styles: { fontSize: 8, cellPadding: 5, overflow: "linebreak", valign: "top", lineColor: [220, 216, 206], lineWidth: 0.5 },
    headStyles: { fillColor: ORANGE, textColor: 255, fontStyle: "bold", valign: "middle" },
    alternateRowStyles: { fillColor: [247, 245, 239] },
    columnStyles: {
      0: { cellWidth: 58 },
      1: { cellWidth: 330 },
      2: { cellWidth: 108 },
      3: { cellWidth: 70, halign: "center" },
      4: { cellWidth: 85, halign: "right" },
      5: { cellWidth: 85, textColor: ORANGE, fontStyle: "bold", halign: "center" },
    },
    didDrawCell: (data) => {
      if (data.section === "body" && data.column.index === 5) {
        const url = urls[data.row.index];
        if (url) doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url });
      }
    },
    didDrawPage: () => {
      const H = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text("Prices are indicative selling prices. Pre-owned / open-box company assets. Enquire for current availability.", 40, H - 20);
    },
  });

  if (enquiry) {
    const priced = items.filter((i) => !i.price_on_request && i.price != null);
    const total = priced.reduce((s, i) => s + Number(i.price) * reqQty(i), 0);
    const hasOnRequest = items.some((i) => i.price_on_request || i.price == null);
    let y = doc.lastAutoTable.finalY + 18;
    const H = doc.internal.pageSize.getHeight();
    if (y > H - 50) { doc.addPage(); y = 60; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...GREEN);
    doc.text(`Indicative total: ${inrPdf(total)}`, 40, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      hasOnRequest
        ? "Indicative only — excludes on-request items; final quote confirmed on enquiry."
        : "Indicative only — final quote confirmed on enquiry.",
      40, y + 14
    );
  }

  const safe = title.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "");
  doc.save(`kapatia-${safe || "catalogue"}-${new Date().toISOString().slice(0, 10)}.pdf`);
};
